import { Sparkles } from 'lucide-react';
import { GoalQuestion } from './questions/GoalQuestion';
import { OutcomeQuestion } from './questions/OutcomeQuestion';
import { DateQuestion } from './questions/DateQuestion';
import { PeopleQuestion } from './questions/PeopleQuestion';
import { FollowUpQuestion } from './questions/FollowUpQuestion';
import { SituationCaptured } from './SituationCaptured';
import { TOTAL_CORE_STEPS } from '../utils/describe.reducer';
import {
  FOLLOW_UP_QUESTIONS,
  describeStrings,
} from '../utils/describe.strings';
import { useDescribeState } from '../context/DescribeStateContext';

interface DescribePageProps {
  sessionId: string;
}

type StepStatus = 'active' | 'answered' | 'upcoming';

function statusForIndex(
  stepIndex: number,
  currentStepIndex: number
): StepStatus {
  if (stepIndex < currentStepIndex) return 'answered';
  if (stepIndex === currentStepIndex) return 'active';
  return 'upcoming';
}

export function DescribePage({ sessionId }: DescribePageProps) {
  const { state, dispatch } = useDescribeState();
  const { currentStepIndex, isComplete } = state;
  const lastVisibleIndex = isComplete ? TOTAL_CORE_STEPS - 1 : currentStepIndex;

  return (
    <main className="flex-1 w-full max-w-[720px] mx-auto pt-8 pb-[55vh] px-6">
      <div className="mb-12 text-center">
        <Sparkles className="mx-auto h-8 w-8 text-primary mb-4" aria-hidden />
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2">
          {describeStrings.intro.title}
        </h2>
        <p className="text-slate-500 text-base max-w-md mx-auto">
          {describeStrings.intro.subtitle}
        </p>
      </div>

      <div className="relative space-y-8">
        <GoalQuestion
          status={statusForIndex(0, currentStepIndex)}
          isLast={lastVisibleIndex === 0}
          answer={state.goal}
          onSubmit={(payload) => dispatch({ type: 'SUBMIT_GOAL', payload })}
        />
        <OutcomeQuestion
          status={statusForIndex(1, currentStepIndex)}
          isLast={lastVisibleIndex === 1}
          answer={state.outcome}
          onSubmit={(payload) => dispatch({ type: 'SUBMIT_OUTCOME', payload })}
        />
        <DateQuestion
          status={statusForIndex(2, currentStepIndex)}
          isLast={lastVisibleIndex === 2}
          answer={state.date}
          onChange={(answer) =>
            dispatch(
              answer
                ? { type: 'SUBMIT_DATE', payload: answer }
                : { type: 'CLEAR_DATE' }
            )
          }
        />
        <PeopleQuestion
          status={statusForIndex(3, currentStepIndex)}
          isLast={lastVisibleIndex === 3}
          answer={state.people}
          onSubmit={(payload) => dispatch({ type: 'SUBMIT_PEOPLE', payload })}
        />

        {FOLLOW_UP_QUESTIONS.map((question, index) => (
          <FollowUpQuestion
            key={`followup-${index}`}
            question={question}
            index={index}
            status={statusForIndex(TOTAL_CORE_STEPS + index, currentStepIndex)}
            isLast={lastVisibleIndex === TOTAL_CORE_STEPS + index}
            answer={state.followUps[index] ?? null}
            onSubmit={(payload) =>
              dispatch({ type: 'SUBMIT_FOLLOWUP', payload })
            }
          />
        ))}

        {isComplete && <SituationCaptured sessionId={sessionId} />}
      </div>
    </main>
  );
}
